import React, { PropsWithChildren, ReactElement, useEffect } from 'react';

import Cookies from 'js-cookie';

import {
  CustomerLocationContext,
  CustomerType,
  MainContext,
  isActiveLocation,
  isInactiveCustomer,
} from 'app/store/types/user-context-types';
import { Spinner } from 'app/components/spinner';
import { SELECTED_LOCATION_COOKIE, WPBOX_SSOLOGIN_REDIRECT_URL } from 'app/utils/cookies';
import * as SentryUtils from 'app/utils/sentry-utils';
import { ContextSwitch } from 'app/features/context-switch/context-switch';
import { fetchUserContext, redirectToEnterpriseUrl, setCustomerLocation } from 'app/store/actions/user-context-thunks';
import { useAppDispatch, useAppSelector } from 'app/hooks/redux-thunk';
import { locationHasDanishPartner, locationIsEnterprise } from 'app/utils/partner-utils';
import { LocationPicker } from '../location-picker/location-picker';
import { CustomerSegmentPicker } from 'app/pages/customer-segment-picker/customer-segment-picker';

interface Props {}

const ContextProvider: React.FunctionComponent<PropsWithChildren<Props>> = () => {
  const selectedCustomerLocation = useAppSelector((state) => state.userContext.selectedCustomerLocation);
  const customerLocations = useAppSelector((state) => state.userContext.customerLocations);
  const contextUser = useAppSelector((state) => state.userContext.user);
  const selectedCustomerSegment = useAppSelector((state) => state.userContext.selectedCustomerSegment);

  const changingLocation = useAppSelector((state) => state.userContext.changingLocation);
  const isFetchingData = useAppSelector((state) => state.userContext.isFetchingData);
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  const dispatch = useAppDispatch();

  const selectedLocationCookie = Cookies.get(SELECTED_LOCATION_COOKIE);

  // We should render LocationPicker if there's a mismatch between SELECTED_LOCATION_COOKIE and selectedCustomerLocation.
  // A new Location can have been set from an outside LocationPicker setting SELECTED_LOCATION_COOKIE,
  // or SELECTED_LOCATION_COOKIE is from another user login or something
  const locationCookieMismatch =
    selectedLocationCookie &&
    selectedCustomerLocation &&
    isActiveLocation(selectedCustomerLocation) &&
    selectedLocationCookie !== selectedCustomerLocation.siteId.toString();

  const shouldRenderApp = selectedCustomerLocation && !locationCookieMismatch;

  useEffect(() => {
    if (customerLocations.length === 0 && !isFetchingData) {
      dispatch(fetchUserContext());
      return;
    }

    if (changingLocation || shouldRenderApp) {
      return;
    }

    if (selectedLocationCookie) {
      const customerLocation = getCustomerLocationBySiteId(selectedLocationCookie);
      if (customerLocation && customerLocations.length > 0 && customerLocationsContainsSiteId(selectedLocationCookie)) {
        dispatch(setCustomerLocation({ customerLocation, accessToken, contextUser }));
      } else if (customerLocations.length === 1) {
        dispatch(setCustomerLocation({ customerLocation: customerLocations[0], accessToken, contextUser }));
      }
    } else if (customerLocations.length === 1) {
      dispatch(setCustomerLocation({ customerLocation: customerLocations[0], accessToken }));
    }
  }, [customerLocations, isFetchingData, changingLocation, shouldRenderApp, selectedLocationCookie]);

  useEffect(() => {
    if (contextUser) {
      SentryUtils.setUser(contextUser);
    }
  }, [contextUser]);

  // The splash should display only if there are customers who are better served
  // by the new My Company appliaction: norwegian enterprise customers.
  const enterpriseLocations = customerLocations.filter(
    (location) => locationIsEnterprise(location) && !locationHasDanishPartner(location),
  );
  const privateLocations = customerLocations.filter(
    (location) => !(locationIsEnterprise(location) && !locationHasDanishPartner(location)),
  );
  const shouldDisplayCustomerSegmentPicker =
    selectedCustomerSegment === CustomerType.BEDRIFT ||
    (enterpriseLocations.length > 0 && privateLocations.length > 0 && !selectedCustomerSegment);

  // If there are enterprise app locations, but no private ones, simply redirect
  const shouldRedirectToEnterpriseApp =
    !selectedCustomerLocation && enterpriseLocations.length > 0 && privateLocations.length === 0;

  const customerLocationsContainsSiteId = (siteId: string): boolean =>
    customerLocations.some((location) => isActiveLocation(location) && `${location.siteId}` === siteId);

  const getCustomerLocationBySiteId = (siteId: string): CustomerLocationContext | undefined =>
    customerLocations.find((location) => isActiveLocation(location) && `${location.siteId}` === siteId);

  const renderApp = (selectedContext: MainContext) => <ContextSwitch context={selectedContext} />;

  const selectSiteOrRenderSelector = (siteId?: string): ReactElement | null => {
    if (siteId) {
      const customerLocation = getCustomerLocationBySiteId(siteId);
      if (customerLocation && customerLocations.length > 0 && customerLocationsContainsSiteId(siteId)) {
        return <Spinner marginVertical={true} />;
      }

      return customerLocations.length === 1 ? <Spinner marginVertical={true} /> : <LocationPicker />;
    }
    if (customerLocations.length === 1 && !isInactiveCustomer(customerLocations[0])) {
      return <Spinner marginVertical={true} />;
    }
    return <LocationPicker />;
  };

  if (shouldDisplayCustomerSegmentPicker) {
    return <CustomerSegmentPicker variant="all" />;
  }

  if ((customerLocations.length === 0 && !isInactiveCustomer(customerLocations[0])) || isFetchingData) {
    return <Spinner marginVertical={true} />;
  }
  if (changingLocation || locationCookieMismatch) {
    return <LocationPicker />;
  }

  if (shouldRedirectToEnterpriseApp) {
    const customerLocation = customerLocations.filter((loc) => loc.customerType === CustomerType.BEDRIFT)[0];
    return redirectToEnterpriseUrl({ customerLocation });
  }

  if (shouldRenderApp) {
    // Coming from Old wpbox solution
    const redirectUrl = Cookies.get(WPBOX_SSOLOGIN_REDIRECT_URL);
    if (redirectUrl) {
      Cookies.remove(WPBOX_SSOLOGIN_REDIRECT_URL);
      window.location.replace(redirectUrl);
      return null;
    }
    return renderApp(selectedCustomerLocation as MainContext);
  }

  return selectSiteOrRenderSelector(selectedLocationCookie);
};

export { ContextProvider };
